import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Loader2, ScanSearch, ArrowRight, AlertCircle, Lock, Zap, CreditCard, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api, { billingAPI, ley40GateAPI } from '@/lib/api';
import { useTranslation } from '@/i18n/TranslationContext';

const LEY40_KEY = 'ley_40_2024_pr';

export default function RunScan() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanAccess, setScanAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState('');
  // [Sub-stage 2, deliverables 4-5] Per-scan Ley 40 override + covered-systems.
  const [gateEval, setGateEval] = useState(null);          // { in_scope, scope_limit_type, provisional }
  const [overrideMode, setOverrideMode] = useState('profile');  // profile | include | exclude
  const [coveredSystems, setCoveredSystems] = useState([]);     // [{identifier, exclude, basis}]

  useEffect(() => {
    const check = async () => {
      try {
        const res = await billingAPI.canScan();
        setScanAccess(res.data);
      } catch {
        setScanAccess({ can_scan: true, reason: '' });
      }
      setCheckingAccess(false);
    };
    check();
    // Entity determination for the override control (non-fatal on failure).
    ley40GateAPI.get().then(res => setGateEval(res.data.evaluation || null)).catch(() => {});
  }, []);

  // Whether Ley 40 will be in scope for THIS scan given profile + override.
  const ley40InScope = overrideMode === 'include' || (overrideMode === 'profile' && !!gateEval?.in_scope);
  const scopeLimited = ['public_function', 'data_generated'].includes(gateEval?.scope_limit_type);
  const showCoveredSystems = ley40InScope && scopeLimited;

  const addSystem = () => setCoveredSystems(s => [...s, { identifier: '', exclude: false, basis: '' }]);
  const updateSystem = (i, patch) => setCoveredSystems(s => s.map((sys, j) => j === i ? { ...sys, ...patch } : sys));
  const removeSystem = (i) => setCoveredSystems(s => s.filter((_, j) => j !== i));

  const handleScan = async (e) => {
    e.preventDefault();
    if (!url.trim()) { setError('Please enter a URL to scan'); return; }

    // Build the applicability payload additions (backend already accepts them).
    const payload = { target_url: url.trim(), scan_type: 'compliance' };
    if (overrideMode === 'include') payload.per_scan_override = { include_law_keys: [LEY40_KEY] };
    else if (overrideMode === 'exclude') payload.per_scan_override = { exclude_law_keys: [LEY40_KEY] };

    if (showCoveredSystems && coveredSystems.length > 0) {
      // S-5 inversion (mirror the server 422): excluding a system needs a basis.
      for (const sys of coveredSystems) {
        if (sys.exclude && !sys.basis.trim()) {
          setError(t('covered_basis_required'));
          return;
        }
      }
      payload.covered_systems = {
        systems: coveredSystems
          .filter(sys => sys.identifier.trim())
          .map(sys => ({
            identifier: sys.identifier.trim(),
            scope_disposition: sys.exclude ? 'excluded' : 'in_scope_scanned',
            exclusion_basis: sys.exclude ? sys.basis.trim() : null,
          })),
      };
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/scans', payload);
      const scan = response.data.scan;
      toast.success('Scan initiated!', { description: `Scanning ${scan.target_url}` });
      navigate(`/scans/${scan.scan_id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start scan';
      setError(msg);
      toast.error('Scan failed', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const res = await billingAPI.createCheckout(plan);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading('');
    }
  };

  // Paywall view
  if (!checkingAccess && scanAccess && !scanAccess.can_scan && scanAccess.show_paywall) {
    return (
      <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="scan-paywall">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">Run Compliance Scan</h1>
          <p className="text-sm text-muted-foreground mt-1">{scanAccess.reason}</p>
        </div>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Lock className="h-5 w-5 text-vrs-amber" />Subscription Required
            </CardTitle>
            <CardDescription>{scanAccess.reason}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(scanAccess.plans || []).map(plan => (
              <div key={plan.id} className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-4 hover:border-primary/30 transition-[border-color] duration-200">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{plan.name}</p>
                    <p className="text-xs text-muted-foreground">{plan.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-lg font-bold font-mono text-foreground">{plan.price}</p>
                  <Button onClick={() => handleCheckout(plan.id)} disabled={checkoutLoading === plan.id} data-testid={`paywall-${plan.id}`}>
                    {checkoutLoading === plan.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                    {plan.id === 'one_time_scan' ? 'Buy' : 'Subscribe'}
                  </Button>
                </div>
              </div>
            ))}
            <Separator />
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => navigate('/billing')}>
              <CreditCard className="mr-2 h-4 w-4" />View Billing Details
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="run-scan-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('run_scan')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('run_scan_desc')}</p>
      </div>

      {/* Free scan indicator */}
      {scanAccess?.is_free_scan && (
        <div className="flex items-center gap-2 rounded-lg border border-vrs-green/30 bg-vrs-green/5 px-4 py-3">
          <Badge variant="outline" className="border-vrs-green/30 text-vrs-green text-xs">Free</Badge>
          <p className="text-sm text-foreground">Your first scan is free!</p>
        </div>
      )}

      <Card className="border-border/60 max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ScanSearch className="h-5 w-5 text-primary" />{t('run_scan')}
          </CardTitle>
          <CardDescription>{t('scan_disclaimer')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleScan} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="scan-url">{t('target_url')}</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="scan-url"
                  type="text"
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); setError(''); }}
                  data-testid="scan-url-input"
                  className="h-12 pl-10 bg-muted/30 border-border text-base"
                  autoFocus
                />
              </div>
            </div>

            {/* [Sub-stage 2, deliverable 4] Per-scan Ley 40 override. Shows the
                profile determination, then lets the user include/exclude Ley 40
                for THIS target (the entity scanned may differ from the account
                holder). Attaches per_scan_override to create_scan (already accepted). */}
            {gateEval && (
              <div className="rounded-lg border border-border/40 bg-muted/10 p-4 space-y-3" data-testid="per-scan-override">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('override_title')}</p>
                <p className="text-xs text-muted-foreground">
                  {gateEval.provisional && !gateEval.in_scope
                    ? t('override_entity_provisional')
                    : gateEval.in_scope ? t('override_entity_applies') : t('override_entity_not_apply')}
                </p>
                <RadioGroup value={overrideMode} onValueChange={setOverrideMode} className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="profile" data-testid="override-profile" /> {t('override_use_profile')}
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="include" data-testid="override-include" /> {t('override_include')}
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="exclude" data-testid="override-exclude" /> {t('override_exclude')}
                  </label>
                </RadioGroup>
                <p className="text-[10px] text-muted-foreground italic">{t('override_note')}</p>
              </div>
            )}

            {/* [Sub-stage 2, deliverable 5] Covered-systems declaration — only when
                the governing scope is limited (public_function / data_generated).
                S-5 inversion: excluding a system requires a written basis (the
                submit handler blocks it, mirroring the server 422). */}
            {showCoveredSystems && (
              <div className="rounded-lg border border-border/40 bg-muted/10 p-4 space-y-3" data-testid="covered-systems">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('covered_title')}</p>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addSystem} data-testid="covered-add">{t('covered_add')}</Button>
                </div>
                {coveredSystems.map((sys, i) => (
                  <div key={i} className="rounded border border-border/40 px-3 py-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={sys.identifier} onChange={e => updateSystem(i, { identifier: e.target.value })} placeholder={t('covered_identifier')} className="h-8 text-sm" data-testid="covered-identifier" />
                      <Button type="button" size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground" onClick={() => removeSystem(i)}>✕</Button>
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox checked={sys.exclude} onCheckedChange={v => updateSystem(i, { exclude: !!v })} data-testid="covered-exclude" />
                      {t('covered_exclude')}
                    </label>
                    {sys.exclude && (
                      <Textarea value={sys.basis} onChange={e => updateSystem(i, { basis: e.target.value })} placeholder={t('covered_basis')} className="text-xs min-h-[48px]" data-testid="covered-basis" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-destructive" data-testid="scan-error">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              data-testid="start-scan-button"
              className="w-full h-12 text-base font-semibold active:scale-[0.98] transition-[background-color,border-color,transform] duration-200"
              disabled={loading || checkingAccess}
            >
              {loading ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t('start_scan')}...</>
              ) : checkingAccess ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t('loading')}...</>
              ) : (
                <><ScanSearch className="mr-2 h-5 w-5" />{t('start_scan')}<ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </form>

          {/* [Phase 3(c) Stage 3.5, DEF-F4/UI-C] Copy rewritten to the curated
              catalog: the prior copy advertised OWASP ZAP + OSV/CVE checks that
              do NOT run (vuln law suspended, active probing off) and omitted
              Ley 40 / Ley 39. Now t()-driven (EN + ES); no non-running tools, no
              legal basis asserted (Option 1). */}
          <div className="mt-6 rounded-lg border border-border/40 bg-muted/10 p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('what_scan_checks')}</p>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-primary mt-0.5 font-bold">1.</span>{t('scan_check_accessibility')}</li>
              <li className="flex items-start gap-2"><span className="text-primary mt-0.5 font-bold">2.</span>{t('scan_check_privacy')}</li>
              <li className="flex items-start gap-2"><span className="text-primary mt-0.5 font-bold">3.</span>{t('scan_check_cyber')}</li>
            </ul>
            <p className="text-[10px] text-muted-foreground/60 mt-3 italic">{t('scan_check_passive')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
